import { TypeMyAccountPageTilesFields } from '@/core/generated-types';
import React, { FC } from 'react';
import useLocale from '@/hooks/useLocale';
import { Errors } from '@/components';
import { usePageData } from '@/context/PageDataProvider';
import { TypeMyAccountData } from '@/pages/[locale]/account';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { ERROR_KINDS } from '@/core/constants';

import {
  PlayKitsTile,
  SubscriberShopTile,
  ReferAFriendTile,
  CoursePacksTile,
  OrderHistoryTile,
  AccountSettingsTile,
  ReadingSkillSetTile,
} from './components';

import {
  generateTranslationsObject,
  TypeAccountSettingsPageTranslations,
} from '@/utils/translations';

const MyAccountPageTiles: FC<TypeMyAccountPageTilesFields> = ({
  playKitsTile,
  subscriberShopTile,
  referAFriendTile,
  coursePacksTile,
  orderHistoryTile,
  accountSettingsTile,
  readingSkillSetTile,
  translations,
}) => {
  const locale = useLocale();

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  const pageData = usePageData() as TypeMyAccountData;

  if (!_isPageDataValid(pageData)) {
    return <Errors errors={errors} heading={heading} />;
  }

  const {
    user,
    hasCourses,
    skillSets,
    hasFilteredSubscriptions,
    validSubscriptions,
  } = pageData;
  const t = generateTranslationsObject(
    translations.fields,
  ) as TypeAccountSettingsPageTranslations;

  return (
    <div className="container mx-auto mb-32 mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="flex flex-col gap-6">
        <PlayKitsTile
          t={t}
          userUuid={user.uuid}
          hasFilteredSubscriptions={hasFilteredSubscriptions}
          tile={playKitsTile.fields}
          locale={locale}
        />
        <SubscriberShopTile
          tile={subscriberShopTile.fields}
          hasValidSubscriptions={validSubscriptions.length > 0}
        />
        {/* Desktop Referral tile */}
        <ReferAFriendTile
          t={t}
          userUuid={user.uuid}
          tile={referAFriendTile.fields}
          locale={locale}
          classes="hidden lg:block"
        />
      </div>
      <div className="flex flex-col gap-6">
        <ReadingSkillSetTile
          t={t}
          tile={readingSkillSetTile.fields}
          hasSkillSets={skillSets.length > 0}
        />
        <CoursePacksTile
          tile={coursePacksTile.fields}
          hasCourses={hasCourses}
        />
        <OrderHistoryTile
          t={t}
          userUuid={user.uuid}
          tile={orderHistoryTile.fields}
          locale={locale}
        />
        <AccountSettingsTile tile={accountSettingsTile.fields} />
        {/* Mobile Referral tile */}
        <ReferAFriendTile
          t={t}
          userUuid={user.uuid}
          tile={referAFriendTile.fields}
          locale={locale}
          classes="lg:hidden"
        />
      </div>
    </div>
  );
};

function _isPageDataValid(pageData: TypeMyAccountData) {
  // The user is the only data type that should be consistently available across all page states.
  // To facilitate null checks we will do this before rendering any of the page tiles so the tiles can
  // depend on this data being there. The tiles should support null checks for all other dependent data.
  return pageData?.user;
}

export default MyAccountPageTiles;
